import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, IconButton, Tooltip, Paper, List, ListItemText, ListItemIcon, Divider, Avatar, Stack, TextField, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Badge, Alert, LinearProgress, Fab, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Accordion, AccordionSummary, AccordionDetails, Snackbar } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  LocalHospital as DoctorIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Pets as PetsIcon,
  LocalHospital as HospitalIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  MoreVert as MoreIcon,
  CalendarToday as CalendarIcon,
  Work as WorkIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import StickyFilters from '@shared/StickyFilters';
import ActionButtons from '@shared/ActionButtons';
import { veterinariansService, CreateVeterinarianData } from '../../services/veterinariansService';

interface Doctor {
  id: string;
  userId: string;
  displayName: string;
  specialities: string[];
  locations: string[];
  status: 'active' | 'inactive' | 'on_leave' | 'pending';
  email: string;
  phone: string;
   rating: number;
  totalAppointments: number;
  experience: number;
  nextAvailableSlot?: string;
  currentSchedule?: {
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`doctors-tabpanel-${index}`}
      aria-labelledby={`doctors-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DoctorsManagement: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newDoctor, setNewDoctor] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    experience: '',
    status: 'active',
    specializations: [] as string[],
    locations: [] as string[],
    weekdayStart: '',
    weekdayEnd: '',
    saturdayStart: '',
    saturdayEnd: '',
    notes: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Incarcare doctori cu API real
  useEffect(() => {
    const loadDoctors = async () => {
      if (!user?.clinicId) return;
      
      setLoading(true);
      
      try {
        // Pentru clinic admin, arată toți doctorii (active și pending)
        const statusFilter = selectedStatus === 'all' ? undefined : (selectedStatus as 'active' | 'inactive' | 'pending' | undefined);
        
        const veterinariansResponse = await veterinariansService.getVeterinarians({
          clinicId: user.clinicId,
          status: statusFilter
        });
        
        // Transformam datele in formatul componentei
        const transformedDoctors: Doctor[] = veterinariansResponse.map((vet: any) => ({
          id: vet.id,
          userId: vet.userId || vet.id,
          displayName: `${vet.firstName || ''} ${vet.lastName || ''}`.trim() || vet.name || 'Doctor necunoscut',
          specialities: vet.specializations || vet.specialities || [],
          locations: vet.locations || [],
          status: vet.status || 'active',
          email: vet.email || '',
          phone: vet.phone || '',
          rating: vet.rating || 5.0,
          totalAppointments: vet.totalAppointments || 0,
          experience: vet.experience || vet.yearsExperience || 0,
          nextAvailableSlot: vet.nextAvailableSlot,
          currentSchedule: vet.schedule || vet.currentSchedule
        }));
        
        setDoctors(transformedDoctors);
        
      } catch (error) {
        console.error('Eroare la incarcarea doctorilor:', error);
        setDoctors([]);
      }

      setLoading(false);
    };

    loadDoctors();
  }, [selectedStatus, user?.clinicId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'on_leave':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatSchedule = (schedule?: any) => {
    if (!schedule) return 'Orar necunoscut';
    
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const weekend = ['saturday', 'sunday'];
    
    // Check if all weekdays have the same schedule
    const weekdaySchedule = schedule[weekdays[0]];
    const isWeekdayUniform = weekdays.every(day => schedule[day] === weekdaySchedule);
    
    // Check if all weekend days have the same schedule
    const weekendSchedule = schedule[weekend[0]];
    const isWeekendUniform = weekend.every(day => schedule[day] === weekendSchedule);
    
    if (isWeekdayUniform && isWeekendUniform && weekdaySchedule === weekendSchedule) {
      return weekdaySchedule === 'Liber' ? 'Liber' : `L-D: ${weekdaySchedule}`;
    }
    
    if (isWeekdayUniform && isWeekendUniform) {
      return `L-V: ${weekdaySchedule} • S-D: ${weekendSchedule}`;
    }
    
    if (isWeekdayUniform) {
      return `L-V: ${weekdaySchedule} • S: ${weekendSchedule}`;
    }
    
    // Fallback to detailed schedule
    return 'Orar personalizat';
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Activ';
      case 'inactive':
        return 'Inactiv';
      case 'on_leave':
        return 'In Concediu';
      default:
        return status;
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateDoctor = () => {
    setCreateDialogOpen(true);
  };

  const handleViewSchedule = (_doctorId: string) => {
    // Implementare vizualizare orar doctor
    // Aici se poate deschide un dialog sau naviga la o pagina cu orarul doctorului
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewDoctor({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      experience: '',
      status: 'active',
      specializations: [],
      locations: [],
      weekdayStart: '',
      weekdayEnd: '',
      saturdayStart: '',
      saturdayEnd: '',
      notes: ''
    });
  };

  const handleSaveDoctor = async () => {
    if (!user?.clinicId) {
      console.error('❌ ID-ul clinicii nu este disponibil');
      alert('Eroare: ID-ul clinicii nu este disponibil');
      return;
    }

    try {
      const veterinarianData: CreateVeterinarianData = {
        firstName: newDoctor.firstName,
        lastName: newDoctor.lastName,
        email: newDoctor.email,
        phone: newDoctor.phone,
        clinicId: user.clinicId,
        specializations: newDoctor.specializations,
        experienceYears: parseInt(newDoctor.experience) || 0,
        description: newDoctor.notes
      };

      const createdVeterinarian = await veterinariansService.createVeterinarian(veterinarianData);

      // Transformăm datele în formatul componentei
      const newDoctorData: Doctor = {
        id: createdVeterinarian.id,
        userId: createdVeterinarian.id,
        displayName: `${createdVeterinarian.firstName} ${createdVeterinarian.lastName}`,
        specialities: veterinarianData.specializations || [],
        locations: newDoctor.locations,
        status: 'pending',
        email: createdVeterinarian.email,
        phone: createdVeterinarian.phone || '',
        rating: 0,
        totalAppointments: 0,
        experience: veterinarianData.experienceYears || 0,
        currentSchedule: {
          monday: newDoctor.weekdayStart && newDoctor.weekdayEnd ? `${newDoctor.weekdayStart}-${newDoctor.weekdayEnd}` : 'Liber',
          tuesday: newDoctor.weekdayStart && newDoctor.weekdayEnd ? `${newDoctor.weekdayStart}-${newDoctor.weekdayEnd}` : 'Liber',
          wednesday: newDoctor.weekdayStart && newDoctor.weekdayEnd ? `${newDoctor.weekdayStart}-${newDoctor.weekdayEnd}` : 'Liber',
          thursday: newDoctor.weekdayStart && newDoctor.weekdayEnd ? `${newDoctor.weekdayStart}-${newDoctor.weekdayEnd}` : 'Liber',
          friday: newDoctor.weekdayStart && newDoctor.weekdayEnd ? `${newDoctor.weekdayStart}-${newDoctor.weekdayEnd}` : 'Liber',
          saturday: newDoctor.saturdayStart && newDoctor.saturdayEnd ? `${newDoctor.saturdayStart}-${newDoctor.saturdayEnd}` : 'Liber',
          sunday: 'Liber'
        }
      };

      // Actualizare optimistă: arătăm imediat în UI noul doctor
      setDoctors([...doctors, newDoctorData]);

      // Reîncărcăm lista de doctori din backend pentru a include doctorul nou creat
      // Reîncărcăm manual lista de doctori
      try {
        // Pentru clinic admin, arată toți doctorii (active și pending)
        const statusFilter = selectedStatus === 'all' ? undefined : (selectedStatus as 'active' | 'inactive' | 'pending' | undefined);
        
        const veterinariansResponse = await veterinariansService.getVeterinarians({
          clinicId: user.clinicId,
          status: statusFilter
        });
        
        // Transformam datele in formatul componentei
        const transformedDoctors: Doctor[] = veterinariansResponse.map((vet: any) => {
          return {
            id: vet.id,
            userId: vet.userId || vet.id,
            displayName: `${vet.firstName || ''} ${vet.lastName || ''}`.trim() || vet.name || 'Doctor necunoscut',
            specialities: vet.specializations || vet.specialities || [],
            locations: vet.locations || [],
            status: vet.status || 'active',
            email: vet.email || '',
            phone: vet.phone || '',
            rating: vet.rating || 5.0,
            totalAppointments: vet.totalAppointments || 0,
            experience: vet.experience || vet.yearsExperience || 0,
            nextAvailableSlot: vet.nextAvailableSlot,
            currentSchedule: vet.schedule || vet.currentSchedule
          };
        });

        setDoctors(transformedDoctors);
        
        // Resetăm filtrele și paginarea pentru a arăta noul doctor
        setPage(0);
        setSearchTerm('');
        setSelectedStatus('all');
        setSelectedLocation('all');
        setTabValue(0); // Mergi la tab-ul "Toți Doctorii"
      } catch (error) {
        console.error('Eroare la reîncărcarea doctorilor:', error);
      }
      
      // Închidem dialogul și resetăm formularul
      setCreateDialogOpen(false);
      setNewDoctor({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        experience: '',
        status: 'active',
        specializations: [],
        locations: [],
        weekdayStart: '',
        weekdayEnd: '',
        saturdayStart: '',
        saturdayEnd: '',
        notes: ''
      });
      
      showSnackbar('Doctor creat cu succes! Un email de activare a fost trimis.', 'success');
      
    } catch (error) {
      console.error('❌ Eroare la crearea doctorului:', error);
      showSnackbar('Eroare la crearea doctorului: ' + (error as Error).message, 'error');
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredDoctors = doctors.filter(doctor => {
    const matchesSearch = doctor.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doctor.specialities.some(spec => spec.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = selectedStatus === 'all' || doctor.status === selectedStatus;
    const matchesLocation = selectedLocation === 'all' || doctor.locations.includes(selectedLocation);
    
    return matchesSearch && matchesStatus && matchesLocation;
  });

  const activeDoctors = filteredDoctors.filter(d => d.status === 'active');
  const inactiveDoctors = filteredDoctors.filter(d => d.status === 'inactive');
  const onLeaveDoctors = filteredDoctors.filter(d => d.status === 'on_leave');
  const allDoctors = filteredDoctors;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se incarca doctorii...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Doctori & Orar
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Personal medical si disponibilitate - profil, specializari, orar, locatii
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateDoctor}
          sx={{ borderRadius: 2 }}
        >
          Doctor Nou
        </Button>
      </Box>

      {/* Filtre */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                placeholder="Cauta nume sau specializare..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="active">Activ</MenuItem>
                  <MenuItem value="inactive">Inactiv</MenuItem>
                  <MenuItem value="on_leave">In Concediu</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Locatie</InputLabel>
                <Select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  label="Locatie"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="Sediu Principal">Sediu Principal</MenuItem>
                  <MenuItem value="Sucursala 1">Sucursala 1</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                fullWidth
                sx={{ height: 56, borderRadius: 2 }}
              >
                Filtre Avansate
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Tooltip title="Actualizeaza">
                <IconButton onClick={() => window.location.reload()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="doctors tabs">
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Activi
                  <Badge badgeContent={activeDoctors.length} color="success" />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  In Concediu
                  <Badge badgeContent={onLeaveDoctors.length} color="info" />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Inactivi
                  <Badge badgeContent={inactiveDoctors.length} color="warning" />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Toti
                  <Badge badgeContent={allDoctors.length} color="default" />
                </Box>
              } 
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Doctor</TableCell>
                  <TableCell>Specializari</TableCell>
                  <TableCell>Locatii</TableCell>
                  <TableCell>Orar</TableCell>
                  <TableCell>Statistici</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeDoctors
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((doctor) => (
                    <TableRow 
                      key={doctor.id} 
                      hover
                      sx={{
                        '&:hover .quick-appointment': {
                          opacity: 1
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.light' }}>
                            <DoctorIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {doctor.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {doctor.experience} ani experienta
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {doctor.specialities.map((spec, index) => (
                            <Chip key={index} label={spec} size="small" color="secondary" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {doctor.locations.map((location, index) => (
                            <Chip key={index} label={location} size="small" color="info" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatSchedule(doctor.currentSchedule)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon fontSize="small" color="warning" />
                            {doctor.rating}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doctor.totalAppointments} programari
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(doctor.status)}
                          color={getStatusColor(doctor.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <ActionButtons
                            onView={() => {}}
                            onEdit={() => {}}
                            customActions={[
                              {
                                icon: <ScheduleIcon />,
                                tooltip: 'Vezi orar',
                                onClick: () => handleViewSchedule(doctor.id)
                              }
                            ]}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            className="quick-appointment"
                            sx={{
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              ml: 1
                            }}
                          >
                            Programare
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={activeDoctors.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Doctor</TableCell>
                  <TableCell>Specializari</TableCell>
                  <TableCell>Locatii</TableCell>
                  <TableCell>Orar</TableCell>
                  <TableCell>Statistici</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {onLeaveDoctors
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((doctor) => (
                    <TableRow 
                      key={doctor.id} 
                      hover
                      sx={{
                        '&:hover .quick-appointment': {
                          opacity: 1
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'info.light' }}>
                            <DoctorIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {doctor.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {doctor.experience} ani experienta
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {doctor.specialities.map((spec, index) => (
                            <Chip key={index} label={spec} size="small" color="secondary" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {doctor.locations.map((location, index) => (
                            <Chip key={index} label={location} size="small" color="info" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          In concediu
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon fontSize="small" color="warning" />
                            {doctor.rating}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doctor.totalAppointments} programari
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(doctor.status)}
                          color={getStatusColor(doctor.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <ActionButtons
                            onView={() => {}}
                            onEdit={() => {}}
                            customActions={[
                              {
                                icon: <ScheduleIcon />,
                                tooltip: 'Vezi orar',
                                onClick: () => handleViewSchedule(doctor.id)
                              }
                            ]}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            className="quick-appointment"
                            sx={{
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              ml: 1
                            }}
                          >
                            Programare
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={onLeaveDoctors.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Doctor</TableCell>
                  <TableCell>Specializari</TableCell>
                  <TableCell>Locatii</TableCell>
                  <TableCell>Orar</TableCell>
                  <TableCell>Statistici</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inactiveDoctors
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((doctor) => (
                    <TableRow 
                      key={doctor.id} 
                      hover
                      sx={{
                        '&:hover .quick-appointment': {
                          opacity: 1
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'warning.light' }}>
                            <DoctorIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {doctor.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {doctor.experience} ani experienta
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {doctor.specialities.map((spec, index) => (
                            <Chip key={index} label={spec} size="small" color="secondary" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {doctor.locations.map((location, index) => (
                            <Chip key={index} label={location} size="small" color="info" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          Inactiv
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon fontSize="small" color="warning" />
                            {doctor.rating}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doctor.totalAppointments} programari
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(doctor.status)}
                          color={getStatusColor(doctor.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <ActionButtons
                            onView={() => {}}
                            onEdit={() => {}}
                            customActions={[
                              {
                                icon: <ScheduleIcon />,
                                tooltip: 'Vezi orar',
                                onClick: () => handleViewSchedule(doctor.id)
                              }
                            ]}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            className="quick-appointment"
                            sx={{
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              ml: 1
                            }}
                          >
                            Programare
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={inactiveDoctors.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Doctor</TableCell>
                  <TableCell>Specializari</TableCell>
                  <TableCell>Locatii</TableCell>
                  <TableCell>Orar</TableCell>
                  <TableCell>Statistici</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allDoctors
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((doctor) => (
                    <TableRow 
                      key={doctor.id} 
                      hover
                      sx={{
                        '&:hover .quick-appointment': {
                          opacity: 1
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: doctor.status === 'active' ? 'primary.light' : 
                                    doctor.status === 'on_leave' ? 'info.light' : 'warning.light'
                          }}>
                            <DoctorIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {doctor.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {doctor.experience} ani experienta
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {doctor.specialities.map((spec, index) => (
                            <Chip key={index} label={spec} size="small" color="secondary" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {doctor.locations.map((location, index) => (
                            <Chip key={index} label={location} size="small" color="info" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {doctor.status === 'active' ? 'L-V: 08:00-16:00' : 'Inactiv'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doctor.status === 'active' ? 'S: 08:00-12:00' : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon fontSize="small" color="warning" />
                            {doctor.rating}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doctor.totalAppointments} programari
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(doctor.status)}
                          color={getStatusColor(doctor.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <ActionButtons
                            onView={() => {}}
                            onEdit={() => {}}
                            customActions={[
                              {
                                icon: <ScheduleIcon />,
                                tooltip: 'Vezi orar',
                                onClick: () => handleViewSchedule(doctor.id)
                              }
                            ]}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            className="quick-appointment"
                            sx={{
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              ml: 1
                            }}
                          >
                            Programare
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={allDoctors.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>
      </Card>

      {/* Create Doctor Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight={600}>
            Doctor Nou
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Personal Information */}
            <Grid size={12}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Informatii Personale
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nume"
                value={newDoctor.firstName || ''}
                onChange={(e) => {
                  setNewDoctor({...newDoctor, firstName: e.target.value});
                }}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Prenume"
                value={newDoctor.lastName || ''}
                onChange={(e) => setNewDoctor({...newDoctor, lastName: e.target.value})}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newDoctor.email || ''}
                onChange={(e) => setNewDoctor({...newDoctor, email: e.target.value})}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Telefon"
                value={newDoctor.phone || ''}
                onChange={(e) => setNewDoctor({...newDoctor, phone: e.target.value})}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Ani Experienta"
                type="number"
                value={newDoctor.experience || ''}
                onChange={(e) => setNewDoctor({...newDoctor, experience: e.target.value})}
                inputProps={{ min: 0, max: 50 }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newDoctor.status || 'active'}
                  onChange={(e) => setNewDoctor({...newDoctor, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value="active">Activ</MenuItem>
                  <MenuItem value="inactive">Inactiv</MenuItem>
                  <MenuItem value="on_leave">In concediu</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Specializations */}
            <Grid size={12}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                Specializari
              </Typography>
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Specializari</InputLabel>
                <Select
                  multiple
                  value={newDoctor.specializations || []}
                  onChange={(e) => setNewDoctor({...newDoctor, specializations: e.target.value as string[]})}
                  label="Specializari"
                >
                  <MenuItem value="chirurgie">Chirurgie</MenuItem>
                  <MenuItem value="ortopedie">Ortopedie</MenuItem>
                  <MenuItem value="cardiologie">Cardiologie</MenuItem>
                  <MenuItem value="dermatologie">Dermatologie</MenuItem>
                  <MenuItem value="oftalmologie">Oftalmologie</MenuItem>
                  <MenuItem value="neurologie">Neurologie</MenuItem>
                  <MenuItem value="medicina_interna">Medicina Interna</MenuItem>
                  <MenuItem value="anestezie">Anestezie</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Locations */}
            <Grid size={12}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                Locatii de Lucru
              </Typography>
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Locatii</InputLabel>
                <Select
                  multiple
                  value={newDoctor.locations || []}
                  onChange={(e) => setNewDoctor({...newDoctor, locations: e.target.value as string[]})}
                  label="Locatii"
                >
                  <MenuItem value="sediu_principal">Sediu Principal</MenuItem>
                  <MenuItem value="sucursala_1">Sucursala 1</MenuItem>
                  <MenuItem value="sucursala_2">Sucursala 2</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Schedule */}
            <Grid size={12}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 2 }}>
                Program de Lucru
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Ora inceput (L-V)"
                type="time"
                value={newDoctor.weekdayStart || ''}
                onChange={(e) => setNewDoctor({...newDoctor, weekdayStart: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Ora sfarsit (L-V)"
                type="time"
                value={newDoctor.weekdayEnd || ''}
                onChange={(e) => setNewDoctor({...newDoctor, weekdayEnd: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Ora inceput (Sambata)"
                type="time"
                value={newDoctor.saturdayStart || ''}
                onChange={(e) => setNewDoctor({...newDoctor, saturdayStart: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Ora sfarsit (Sambata)"
                type="time"
                value={newDoctor.saturdayEnd || ''}
                onChange={(e) => setNewDoctor({...newDoctor, saturdayEnd: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Notes */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Observatii"
                multiline
                rows={3}
                value={newDoctor.notes || ''}
                onChange={(e) => setNewDoctor({...newDoctor, notes: e.target.value})}
                placeholder="Observatii suplimentare despre doctor..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseCreateDialog} color="inherit">
            Anuleaza
          </Button>
          <Button 
            onClick={handleSaveDoctor} 
            variant="contained" 
            color="primary"
            disabled={!newDoctor.firstName || !newDoctor.lastName || !newDoctor.email || !newDoctor.phone}
          >
            Creeaza Doctorul
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pentru notificări */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DoctorsManagement;

